<template>
  <div class="analysis-agent-chat" :class="{ expanded: isExpanded }">
    <!-- Chat Toggle Button -->
    <button v-if="!isExpanded" @click="toggleChat" class="chat-toggle">
      <i class="fas fa-robot"></i>
      <span class="toggle-label">Ask Agent</span>
    </button>

    <!-- Chat Panel -->
    <div v-if="isExpanded" class="chat-panel">
      <div class="chat-header">
        <div class="header-title">
          <i class="fas fa-robot"></i>
          <span>Analysis Assistant</span>
        </div>
        <div class="header-actions">
          <button @click="clearChat" class="btn-icon" title="Clear chat">
            <i class="fas fa-trash-alt"></i>
          </button>
          <button @click="toggleChat" class="btn-icon" title="Minimize">
            <i class="fas fa-minus"></i>
          </button>
        </div>
      </div>

      <div class="chat-messages" ref="messagesContainer">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="welcome-message">
          <div class="welcome-icon">
            <i class="fas fa-robot"></i>
          </div>
          <h4>Analysis Assistant</h4>
          <p>I can help you correct analysis results and improve prompts. Try:</p>
          <div class="suggestions">
            <button @click="sendSuggestion('This result is wrong - it should have passed')" class="suggestion-btn">
              This should have passed
            </button>
            <button @click="sendSuggestion('The AI missed evidence on page 2')" class="suggestion-btn">
              AI missed evidence
            </button>
            <button @click="sendSuggestion('Why did this requirement fail?')" class="suggestion-btn">
              Why did this fail?
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div 
          v-for="(message, index) in messages" 
          :key="index" 
          class="message"
          :class="message.role"
        >
          <div class="message-avatar">
            <i :class="message.role === 'user' ? 'fas fa-user' : 'fas fa-robot'"></i>
          </div>
          <div class="message-content">
            <div class="message-text" v-html="formatMessage(message.content)"></div>
            
            <!-- Suggested Correction Preview -->
            <div v-if="message.suggestedCorrection" class="correction-preview">
              <div class="correction-header">
                <i class="fas fa-lightbulb"></i> Suggested Correction
              </div>
              <div class="correction-details">
                <div class="correction-item">
                  <span class="label">Should be:</span>
                  <span :class="['result-badge', message.suggestedCorrection.correct_result_should_be?.toLowerCase()]">
                    {{ message.suggestedCorrection.correct_result_should_be }}
                  </span>
                </div>
                <div v-if="message.suggestedCorrection.correction_categories?.length" class="correction-item">
                  <span class="label">Issue type:</span>
                  <span class="categories">{{ message.suggestedCorrection.correction_categories.join(', ') }}</span>
                </div>
              </div>
            </div>
            
            <!-- Action Buttons if present -->
            <div v-if="message.actions?.length" class="message-actions">
              <button 
                v-for="(action, actionIndex) in message.actions" 
                :key="actionIndex"
                @click="executeAction(action)"
                class="action-btn"
                :class="action.type"
                :disabled="action.executed"
              >
                <i :class="action.icon || 'fas fa-check'"></i>
                {{ action.label }}
                <span v-if="action.executed" class="executed-badge">Done</span>
              </button>
            </div>

            <div class="message-time">{{ formatTime(message.timestamp) }}</div>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-if="isTyping" class="message assistant typing">
          <div class="message-avatar">
            <i class="fas fa-robot"></i>
          </div>
          <div class="message-content">
            <div class="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="chat-input">
        <textarea 
          v-model="inputMessage"
          @keydown.enter.prevent="sendMessage"
          placeholder="Describe what's wrong with this result..."
          ref="inputField"
          rows="2"
        ></textarea>
        <button @click="sendMessage" :disabled="!inputMessage.trim() || isTyping" class="send-btn">
          <i class="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, nextTick, watch } from 'vue'
import api from '@/services/api'
import { sanitizeLlmResponse } from '@/utils/sanitize'

export default {
  name: 'AnalysisAgentChat',
  
  props: {
    // The current requirement result being viewed
    requirementResult: {
      type: Object,
      default: null
    },
    // The check run context
    checkRun: {
      type: Object,
      default: null
    },
    // The document being analyzed
    document: {
      type: Object,
      default: null
    },
    // Project ID for context
    projectId: {
      type: [String, Number],
      default: null
    }
  },
  
  emits: ['correction-submitted', 'prompt-updated', 'refresh-results'],
  
  setup(props, { emit }) {
    const isExpanded = ref(false)
    const messages = ref([])
    const inputMessage = ref('')
    const isTyping = ref(false)
    const messagesContainer = ref(null)
    const inputField = ref(null)
    
    const toggleChat = () => {
      isExpanded.value = !isExpanded.value
      if (isExpanded.value) {
        nextTick(() => {
          inputField.value?.focus()
        })
      }
    }
    
    const clearChat = () => {
      messages.value = []
    }
    
    const scrollToBottom = () => {
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }
    
    const formatMessage = (content) => {
      return sanitizeLlmResponse(content)
    }
    
    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    
    const sendSuggestion = (text) => {
      inputMessage.value = text
      sendMessage()
    }
    
    const sendMessage = async () => {
      const text = inputMessage.value.trim()
      if (!text || isTyping.value) return
      
      // Add user message
      messages.value.push({
        role: 'user',
        content: text,
        timestamp: new Date().toISOString()
      })
      
      inputMessage.value = ''
      scrollToBottom()
      
      // Show typing indicator
      isTyping.value = true
      
      try {
        // Build context from props
        const context = {
          requirement_result: props.requirementResult ? {
            id: props.requirementResult.id,
            requirement_code: props.requirementResult.requirement_code,
            requirement_name: props.requirementResult.requirement_name,
            passed: props.requirementResult.passed,
            evidence: props.requirementResult.evidence,
            details: props.requirementResult.details
          } : null,
          check_run: props.checkRun ? {
            id: props.checkRun.id,
            check_definition_name: props.checkRun.check_definition_name,
            outcome: props.checkRun.outcome
          } : null,
          document: props.document ? {
            id: props.document.id,
            name: props.document.name,
            file_type: props.document.file_type
          } : null,
          history: messages.value.slice(-10).map(m => ({
            role: m.role,
            content: m.content
          }))
        }
        
        // Call the agent API
        const response = await api.post('/compliance-health/agent/analyze-result/', {
          message: text,
          project_id: props.projectId,
          requirement_result_id: props.requirementResult?.id,
          context
        })
        
        // Add assistant response
        const assistantMessage = {
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date().toISOString(),
          actions: response.data.actions || [],
          suggestedCorrection: response.data.suggested_correction || null
        }
        
        messages.value.push(assistantMessage)
        
      } catch (error) {
        console.error('Error sending message:', error)
        messages.value.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date().toISOString()
        })
      } finally {
        isTyping.value = false
        scrollToBottom()
      }
    }
    
    const executeAction = async (action) => {
      if (action.executed) return
      
      try {
        if (action.type === 'submit_correction') {
          // Submit the correction to the API
          const response = await api.patch(`/requirement-results/${props.requirementResult.id}/feedback/`, {
            accuracy: action.feedback.accuracy,
            correct_result_should_be: action.feedback.correct_result_should_be,
            comment: action.feedback.comment,
            correction_categories: action.feedback.correction_categories || [],
            correct_evidence_should_be: action.feedback.correct_evidence_should_be || ''
          })
          
          action.executed = true
          
          // Add confirmation message
          messages.value.push({
            role: 'assistant',
            content: `✅ **Correction submitted successfully!**\n\nThe result has been marked as "${action.feedback.accuracy}" and should be "${action.feedback.correct_result_should_be}".\n\nThis feedback will be used to improve future analysis.`,
            timestamp: new Date().toISOString()
          })
          
          // Emit event to parent
          emit('correction-submitted', response.data)
          
        } else if (action.type === 'suggest_prompt_improvement') {
          // Request a prompt improvement suggestion
          const response = await api.post('/compliance-health/agent/suggest-prompt/', {
            requirement_result_id: props.requirementResult?.id,
            issue_description: action.issueDescription
          })
          
          action.executed = true
          
          // Add the suggestion as a message
          messages.value.push({
            role: 'assistant',
            content: response.data.suggestion,
            timestamp: new Date().toISOString(),
            actions: response.data.actions || []
          })
          
        } else if (action.type === 'apply_prompt_improvement') {
          // Apply the suggested prompt improvement and optionally trigger re-analysis
          const response = await api.post('/compliance-health/agent/apply-prompt/', {
            requirement_id: action.requirementId,
            enhanced_prompt: action.enhancedPrompt,
            issue_description: action.issueDescription || '',
            requirement_result_id: props.requirementResult?.id,
            trigger_reanalysis: action.triggerReanalysis !== false // Default to true
          })
          
          action.executed = true
          
          let message = `✅ **Prompt updated!**\n\nThe enhanced prompt has been saved.`
          
          if (response.data.reanalysis_queued) {
            message += `\n\n🔄 **Re-analysis queued!** The document is being re-analyzed with the improved prompt. Check back in a moment to see if the result changed.`
            
            // Add action to check reanalysis status
            messages.value.push({
              role: 'assistant',
              content: message,
              timestamp: new Date().toISOString(),
              actions: [{
                type: 'check_reanalysis',
                label: 'Check Re-analysis Result',
                icon: 'fas fa-sync',
                taskId: response.data.task_id,
                requirementResultId: props.requirementResult?.id
              }]
            })
          } else {
            message += `\n\nFuture analyses will use the improved version.`
            messages.value.push({
              role: 'assistant',
              content: message,
              timestamp: new Date().toISOString()
            })
          }
          
          emit('prompt-updated', response.data)
          
        } else if (action.type === 'trigger_reanalysis') {
          // Manually trigger re-analysis
          const response = await api.post('/compliance-health/agent/reanalyze/', {
            requirement_result_id: props.requirementResult?.id,
            document_id: props.document?.id,
            requirement_id: action.requirementId
          })
          
          action.executed = true
          
          messages.value.push({
            role: 'assistant',
            content: `🔄 **Re-analysis queued!**\n\nThe document is being re-analyzed. Task ID: ${response.data.task_id}`,
            timestamp: new Date().toISOString(),
            actions: [{
              type: 'check_reanalysis',
              label: 'Check Result',
              icon: 'fas fa-sync',
              taskId: response.data.task_id,
              requirementResultId: props.requirementResult?.id
            }]
          })
          
        } else if (action.type === 'check_reanalysis') {
          // Check the status/result of a re-analysis task
          // For now, just reload the requirement result to see if it changed
          action.executed = true
          
          messages.value.push({
            role: 'assistant',
            content: `ℹ️ **Checking result...**\n\nPlease close and reopen this analysis view to see the updated result. If the prompt change was successful, the result should reflect the correction.`,
            timestamp: new Date().toISOString()
          })
          
          // Emit event to refresh the parent view
          emit('refresh-results')
          
        } else if (action.type === 'view_document') {
          // Scroll to specific page in document viewer (emit event)
          emit('view-document-page', action.page)
          action.executed = true
        }
        
        scrollToBottom()
        
      } catch (error) {
        console.error('Error executing action:', error)
        messages.value.push({
          role: 'assistant',
          content: `❌ Failed to execute action: ${error.response?.data?.error || error.message}`,
          timestamp: new Date().toISOString()
        })
        scrollToBottom()
      }
    }
    
    // Auto-scroll when messages change
    watch(messages, scrollToBottom, { deep: true })
    
    // When requirement result changes, add context message
    watch(() => props.requirementResult, (newResult) => {
      if (newResult && messages.value.length === 0) {
        // Could add an initial context message here if desired
      }
    }, { immediate: true })
    
    return {
      isExpanded,
      messages,
      inputMessage,
      isTyping,
      messagesContainer,
      inputField,
      toggleChat,
      clearChat,
      formatMessage,
      formatTime,
      sendSuggestion,
      sendMessage,
      executeAction
    }
  }
}
</script>

<style scoped>
.analysis-agent-chat {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  z-index: 100;
}

.chat-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  transition: all 0.2s ease;
  font-weight: 500;
}

.chat-toggle:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.chat-toggle i {
  font-size: 1.1rem;
}

.chat-panel {
  width: 380px;
  height: 450px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.25rem;
}

.btn-icon {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.btn-icon:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: #f8fafc;
}

.welcome-message {
  text-align: center;
  padding: 1.5rem;
}

.welcome-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: white;
  font-size: 1.5rem;
}

.welcome-message h4 {
  margin: 0 0 0.5rem;
  color: #1f2937;
}

.welcome-message p {
  margin: 0 0 1rem;
  color: #64748b;
  font-size: 0.875rem;
}

.suggestions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.suggestion-btn {
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.suggestion-btn:hover {
  background: #f1f5f9;
  border-color: #10b981;
  color: #10b981;
}

.message {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.assistant .message-avatar {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.message.user .message-avatar {
  background: #667eea;
  color: white;
}

.message-content {
  max-width: 85%;
}

.message-text {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.875rem;
  line-height: 1.5;
}

.message.assistant .message-text {
  background: white;
  border: 1px solid #e2e8f0;
}

.message.user .message-text {
  background: #667eea;
  color: white;
}

.message-time {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

.message.user .message-time {
  text-align: right;
}

/* Correction Preview */
.correction-preview {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
}

.correction-header {
  font-weight: 600;
  color: #059669;
  margin-bottom: 0.5rem;
  font-size: 0.8125rem;
}

.correction-header i {
  margin-right: 0.25rem;
}

.correction-details {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.correction-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
}

.correction-item .label {
  color: #64748b;
}

.result-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.75rem;
}

.result-badge.pass {
  background: #dcfce7;
  color: #16a34a;
}

.result-badge.fail {
  background: #fee2e2;
  color: #dc2626;
}

.result-badge.unclear {
  background: #fef3c7;
  color: #d97706;
}

.categories {
  color: #334155;
}

/* Action Buttons */
.message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.action-btn.submit_correction {
  background: #10b981;
  color: white;
}

.action-btn.submit_correction:hover:not(:disabled) {
  background: #059669;
}

.action-btn.suggest_prompt_improvement {
  background: #3b82f6;
  color: white;
}

.action-btn.suggest_prompt_improvement:hover:not(:disabled) {
  background: #2563eb;
}

.action-btn.apply_prompt_improvement {
  background: #8b5cf6;
  color: white;
}

.action-btn.view_document {
  background: white;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.executed-badge {
  background: rgba(255, 255, 255, 0.3);
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  font-size: 0.6875rem;
  margin-left: 0.25rem;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 0.75rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

/* Input Area */
.chat-input {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
  background: white;
}

.chat-input textarea {
  flex: 1;
  padding: 0.625rem 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  resize: none;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.4;
}

.chat-input textarea:focus {
  outline: none;
  border-color: #10b981;
}

.send-btn {
  width: 40px;
  height: 40px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  align-self: flex-end;
}

.send-btn:hover:not(:disabled) {
  background: #059669;
}

.send-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}
</style>

